import { useEffect, useState, type ReactNode } from 'react';
import { RefreshCw } from 'lucide-react';
import {
  operatorConsole,
  type CallbackLogDetail,
} from '../../lib/api/operatorConsole';
import { Button } from '../../components/ui/Button';
import { useMe } from '../../auth/MeContext';
import { ActionBadge } from './ActionBadge';
import { StatusPill } from './StatusPill';
import { HeadersTable } from './HeadersTable';
import { JsonBlock } from './JsonBlock';

interface Props {
  // The id of the log to show. `null` keeps the modal closed (mirrors the
  // CallbackDocsModal `doc`-driven open/close convention).
  logId: number | null;
  onClose: () => void;
  // Fired after a successful resend so the parent list can refetch. Receives
  // the newly-created replay log.
  onResent?: (newLog: CallbackLogDetail) => void;
}

export function CallbackLogDetailModal({ logId, onClose, onResent }: Props) {
  const { isOwner } = useMe();
  const [log, setLog] = useState<CallbackLogDetail | null>(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);

  const [resending, setResending] = useState(false);
  const [resendError, setResendError] = useState<string | null>(null);
  const [resendNotice, setResendNotice] = useState<string | null>(null);

  // Close on Escape — same pattern as CallbackDocsModal.
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    if (logId !== null) {
      document.addEventListener('keydown', handleKeyDown);
      return () => document.removeEventListener('keydown', handleKeyDown);
    }
  }, [logId, onClose]);

  // Fetch the full detail whenever the modal opens for a new id.
  useEffect(() => {
    if (logId === null) {
      setLog(null);
      setLoadError(null);
      setResendError(null);
      setResendNotice(null);
      return;
    }
    let cancelled = false;
    const run = async () => {
      try {
        setLoading(true);
        setLoadError(null);
        const detail = await operatorConsole.getCallbackLog(logId);
        if (!cancelled) setLog(detail);
      } catch (err) {
        if (!cancelled) {
          setLoadError(err instanceof Error ? err.message : 'Unknown error');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    run();
    return () => {
      cancelled = true;
    };
  }, [logId]);

  const handleResend = async () => {
    if (!log) return;
    if (
      !window.confirm(
        `Resend the ${log.action} callback (log #${log.id})? This will replay the request to the operator backend.`,
      )
    ) {
      return;
    }
    try {
      setResending(true);
      setResendError(null);
      setResendNotice(null);
      const newLog = await operatorConsole.resendCallbackLog(log.id);
      setResendNotice(
        `Resent — new log #${newLog.id} (${
          newLog.success ? 'success' : 'failed'
        }${newLog.response_status != null ? `, HTTP ${newLog.response_status}` : ''}).`,
      );
      // Surface the replay in this modal and let the list refresh.
      setLog(newLog);
      onResent?.(newLog);
    } catch (err) {
      // ApiError (incl. 403 forbidden) carries the backend `message`.
      setResendError(err instanceof Error ? err.message : 'Resend failed');
    } finally {
      setResending(false);
    }
  };

  if (logId === null) return null;

  return (
    <>
      {/* Backdrop */}
      <div className="fixed inset-0 z-40 bg-black/30" onClick={onClose} />

      {/* Modal */}
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div
          className="flex max-h-[85vh] w-full max-w-2xl flex-col overflow-hidden rounded-lg bg-[var(--co-surface)] shadow-lg"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="sticky top-0 border-b border-[var(--co-border)] bg-[var(--co-surface)] p-4">
            <div className="flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="text-xs font-mono text-[var(--co-muted)]">
                  Callback Log {log ? `#${log.id}` : `#${logId}`}
                </div>
                <h2 className="text-xl font-bold text-[var(--co-text)]">
                  {log ? (
                    <span className="flex flex-wrap items-center gap-2">
                      <ActionBadge action={log.action} />
                      <StatusPill status={log.response_status} success={log.success} />
                    </span>
                  ) : (
                    'Loading…'
                  )}
                </h2>
                {log && (
                  <p className="mt-1 break-all text-sm text-[var(--co-muted)]">
                    {log.request_method} {log.request_url}
                  </p>
                )}
              </div>
              <button
                onClick={onClose}
                className="shrink-0 text-xl font-semibold text-[var(--co-muted)] hover:text-[var(--co-text)]"
              >
                ×
              </button>
            </div>
          </div>

          {/* Content */}
          <div className="flex-1 space-y-4 overflow-y-auto p-4">
            {loading && <div className="text-sm text-[var(--co-muted)]">Loading log…</div>}
            {loadError && (
              <div className="text-sm text-[var(--co-error)]">Error: {loadError}</div>
            )}

            {log && (
              <>
                {/* Summary */}
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <SummaryItem label="Duration">
                    {log.duration_ms != null ? `${log.duration_ms} ms` : '—'}
                  </SummaryItem>
                  <SummaryItem label="Created">
                    {new Date(log.created_at).toLocaleString()}
                  </SummaryItem>
                  <SummaryItem label="External Tx Id">
                    <span className="font-mono">{log.external_transaction_id || '—'}</span>
                  </SummaryItem>
                  <SummaryItem label="Round Id">
                    <span className="font-mono">{log.round_id || '—'}</span>
                  </SummaryItem>
                  <SummaryItem label="Session Id">
                    {log.session_id != null ? `#${log.session_id}` : '—'}
                  </SummaryItem>
                  <SummaryItem label="Transaction Id">
                    {log.transaction_id != null ? `#${log.transaction_id}` : '—'}
                  </SummaryItem>
                  {log.resent_from_callback_log_id != null && (
                    <SummaryItem label="Resent from">
                      <span className="font-mono">
                        log #{log.resent_from_callback_log_id}
                      </span>
                    </SummaryItem>
                  )}
                </div>

                {/* Transport error (status null) */}
                {(log.error_class || log.error_message) && (
                  <div className="rounded border border-[var(--co-error)] bg-[color-mix(in_srgb,var(--co-error)_8%,transparent)] p-3">
                    <h3 className="mb-1 text-sm font-semibold text-[var(--co-error)]">
                      Transport Error
                    </h3>
                    <div className="font-mono text-xs text-[var(--co-error)]">
                      {log.error_class}
                      {log.error_class && log.error_message ? ': ' : ''}
                      {log.error_message}
                    </div>
                  </div>
                )}

                {/* Request */}
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-[var(--co-text)]">Request</h3>
                  <div className="rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                    {log.request_method} {log.request_url}
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium text-[var(--co-muted)]">
                      Headers
                    </div>
                    <HeadersTable headers={log.request_headers} />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium text-[var(--co-muted)]">
                      Body
                    </div>
                    <JsonBlock value={log.request_body} />
                  </div>
                </section>

                {/* Response */}
                <section className="space-y-2">
                  <h3 className="text-sm font-semibold text-[var(--co-text)]">Response</h3>
                  <div className="rounded bg-[var(--co-bg)] p-3 font-mono text-xs text-[var(--co-text)]">
                    Status: {log.response_status ?? '—'}
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium text-[var(--co-muted)]">
                      Headers
                    </div>
                    <HeadersTable headers={log.response_headers} />
                  </div>
                  <div>
                    <div className="mb-1 text-xs font-medium text-[var(--co-muted)]">
                      Body
                    </div>
                    <JsonBlock value={log.response_body} />
                  </div>
                </section>
              </>
            )}
          </div>

          {/* Footer */}
          {log && (
            <div className="space-y-2 border-t border-[var(--co-border)] bg-[var(--co-surface)] p-4">
              {resendNotice && (
                <div className="text-sm text-[var(--co-success)]">{resendNotice}</div>
              )}
              {resendError && (
                <div className="text-sm text-[var(--co-error)]">{resendError}</div>
              )}
              {/* Reading a log is a `logs` grant; replaying one re-fires a real
                  debit/credit/refund at the casino, so the backend keeps
                  Resend with the owner. Hide it rather than let a team member
                  click straight into a 403. */}
              {log.can_resend && isOwner && (
                <div className="flex justify-end">
                  <Button
                    onClick={handleResend}
                    disabled={resending}
                    className="gap-2"
                  >
                    <RefreshCw
                      size={16}
                      aria-hidden="true"
                      className={resending ? 'animate-spin' : ''}
                    />
                    {resending ? 'Resending…' : 'Resend'}
                  </Button>
                </div>
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}

function SummaryItem({
  label,
  children,
}: {
  label: string;
  children: ReactNode;
}) {
  return (
    <div>
      <div className="text-xs font-medium text-[var(--co-muted)]">{label}</div>
      <div className="text-[var(--co-text)]">{children}</div>
    </div>
  );
}
